/** Readable models the Rewards Hub UI consumes; built from the wire DTOs in mappers.ts. */

/** `BONUS` — the bonus coin, which burns on withdrawal (27.10); USDC stays `USD`. */
export type RewardBonusUnit = 'PCT' | 'USD' | 'BONUS' | 'TOKEN';

/**
 * What a tier, an album, a rank or a rare card pays — one shape for all four.
 * The phrase is built on the client (`useRewardBonusText`), in the player's
 * language.
 */
export interface RewardBonus {
  /** Payout type, e.g. `DEPOSIT_BONUS`, `FIXED_BONUS`, `FIXED_TOKEN`. */
  type: string;
  amount: number;
  unit: RewardBonusUnit | string;
  /** Ceiling of a percentage bonus, in USD; `null` when there is none. */
  max: number | null;
  /**
   * Placeable right now. `false` is a pause, not a refusal: the bonus comes
   * later, so nothing is disabled on it.
   */
  available: boolean;
  /** Days a fee discount or cashback boost lasts; null for other types or when not sent. */
  durationDays: number | null;
}

export interface RewardBadgeRef {
  id: string;
  image: string | null;
}

export interface OnboardingTask {
  index: number;
  code: string;
  /** Localized title from the backend; the code is only an id for icons/analytics. */
  title: string;
  done: boolean;
  /** Done and the reward not taken — straight from the service (`cl`), never inferred. */
  claimable: boolean;
  badge: RewardBadgeRef | null;
  /** The app-only promo code the step asks for; null when there is none to enter. */
  promoCode: string | null;
  /** What the step pays (`bn`); null until the service sends it. */
  bonus: RewardBonus | null;
}

export interface RewardsProfile {
  /** Null before the onboarding gate — no rank, avatar or "points to next" yet. */
  rankIndex: number | null;
  /** Ranks reached whose gift still waits for its Claim, lowest first (`rcl`). */
  claimableRanks: number[];
  points: number;
  /**
   * The points the next rank is reached at (`np`) — a threshold, not what is
   * left: that is this minus `points` (01.10 §6).
   */
  nextRankThreshold: number | null;
  stepsClosed: number;
  avatar: { id: string; frames: AvatarFrameId[] } | null;
  availableAvatarIds: string[];
  glow: { type: string; strength: number } | null;
  onboarding: {
    done: number;
    total: number;
    tasks: OnboardingTask[];
  };
  /** Lifetime streak in days, and today's position in the seven-day week (0 = no streak). */
  streak: {
    current: number;
    weekDay: number;
    /**
     * Whether today is checked in and its card taken, and when the next day
     * opens (`st.ci/pk/nx`, 24.09). `null` — a build that does not send them;
     * then only `/daily` knows.
     */
    checkedIn: boolean | null;
    picked: boolean | null;
    nextDayAt: Date | null;
    /** Today is the rare-card day — day 7 is played on the weekly table. `null` — not sent. */
    rareDay: boolean | null;
  };
}

/** Artifacts a streak day paid (`st.aw`). */
export interface StreakAward {
  kind: 'medals' | 'cups' | 'diamonds';
  quantity: number;
}

/** The streak as the answer to a pick moved it. */
export interface PickStreak {
  current: number;
  weekDay: number;
  awards: StreakAward[];
}

export type DailyDrawKind = 'CARD' | 'DUD' | 'DUPLICATE' | 'JOKER' | 'JOKER_X2' | 'SHUFFLE';

export interface DailyCardBack {
  slot: number;
  /** Present only on a revealed back. */
  kind?: DailyDrawKind;
  /** Index into `DailyDraw.albums`. */
  albumIndex?: number;
  cardId?: string;
  image?: string | null;
}

export interface DailyAlbum {
  code: string;
  position: number;
  /** Today's pick already counted. */
  collected: number;
  total: number;
}

export interface DailyJoker {
  /** Cards still to choose: 1 for JOKER, 2 for JOKER_X2 until the first is taken. */
  left: number;
  /** Album to open, as an index into `DailyDraw.albums`; `null` — either one. */
  albumIndex: number | null;
  /** Ids of the cards that may be chosen. */
  options: string[];
}

export interface DailyDraw {
  /** Shelf complete: nothing is dealt, the check-in still counts. */
  shelfComplete: boolean;
  albums: DailyAlbum[];
  backs: DailyCardBack[];
  hasSpecial: boolean;
  pickedToday: boolean;
  pickedSlot: number | null;
  /** Card ids that went into the album with this answer. Animate these, never `backs[pickedSlot]`. */
  arrived: string[];
  /** After a shuffle: the cards already seen, in no slot order. Non-empty = pick again. */
  seen: string[];
  joker: DailyJoker | null;
  /**
   * Only the answer to a pick carries it; on a plain read the profile owns the
   * streak, so `null` means "ask the profile", not "no streak".
   */
  streak: PickStreak | null;
  nextDrawAt: Date | null;
  /** Today is the rare-card day: nothing is dealt here, the day is played on the weekly table. */
  rareDay: boolean;
}

export type WeeklyPickOutcome = 'GRANTED' | 'ALREADY_HELD' | 'UNAVAILABLE' | 'NONE';

export interface WeeklyCard {
  slot: number;
  /** Present only once the table is turned over. */
  cardId?: string;
  title?: string;
  image?: string | null;
  /** `null` — the card carries no bonus (`NONE`), which is a lasting state, not lost data. */
  bonus?: RewardBonus | null;
}

/** A bonus of a past week still to be claimed. */
export interface WeeklyPendingBonus {
  /** The streak week that earned it — sent back to `claim-bonus`. */
  week: number;
  cardId: string | null;
  title: string;
  image: string | null;
  bonus: RewardBonus | null;
}

export interface WeeklyMoment {
  open: boolean;
  streak: number;
  /** Lifetime number of the rare card on offer. */
  cardNumber: number | null;
  cards: WeeklyCard[];
  /** Bonuses of past weeks, oldest first. They never expire. */
  pendingBonuses: WeeklyPendingBonus[];
  /** What the pick did — only on the answer to one. */
  outcome: WeeklyPickOutcome | null;
  pickedSlot: number | null;
  /** On the answer to a rare-card-day pick only: day 7 closed, and what it paid. */
  pickStreak: PickStreak | null;
  /** On the answer to a pick: the card's promo code in storage (GRANTED / ALREADY_HELD). */
  promoCodeId: string | null;
}

export interface ArtifactCount {
  earned: number;
  spent: number;
  onHand: number;
}

export interface StepBlocker {
  ladderId: string;
  title: string;
  closedTiers: number;
}

/**
 * A step is tier N closed on EVERY active ladder, so a player is normally on
 * step 0 with a long list of blockers — that is the shape of the data, not a
 * shortfall to hide.
 */
export interface Step {
  index: number;
  closed: boolean;
  /** How many ladders are short of this step. */
  gap: number;
  /** Weakest ladders first. */
  blockers: StepBlocker[];
}

/** What a craft turns artifacts into. */
export type CraftTarget = 'cup' | 'relic';

export interface Artifacts {
  medals: ArtifactCount;
  cups: ArtifactCount;
  relics: ArtifactCount;
}

export interface LadderTier {
  index: number;
  threshold: number;
  title: string;
  badge: RewardBadgeRef | null;
  hasBonus: boolean;
  /** What the tier pays; `null` with no bonus, or when the wire only flags one. */
  bonus: RewardBonus | null;
  /** When the player claimed it (`ca`); null while it is not claimed. */
  claimedAt: Date | null;
}

export interface Ladder {
  id: string;
  title: string;
  /** What the ladder counts ("Total trading volume on Prediction Markets, in USD"). */
  description: string | null;
  closedTiers: number;
  metric: string | null;
  current: number;
  /** Absent before the onboarding gate. */
  tiers: LadderTier[] | null;
  /** The top tier waiting for Claim — what Claim sends — if any. */
  claimableTier: number | null;
  /**
   * How many cleared tiers one Claim collects: those ending at `claimableTier`
   * (01.10). `0` with nothing to claim; `1` from a build that does not say.
   */
  claimableCount: number;
  /** Claimed tiers whose promo code is on its way (`pw`). */
  promoPendingTiers: number[];
  imageUrl: string | null;
}

export interface RankClaimResult {
  rankIndex: number;
  /** The promo code the gift placed in storage; `null` — nothing to show in Promo codes. */
  promoCodeId: string | null;
}

export interface LadderClaimedTier {
  tierIndex: number;
  badgeId: string | null;
  promoCodeId: string | null;
  /** The tier is claimed, its promo code follows later. */
  promoPending: boolean;
}

export interface LadderClaimResult {
  ladderId: string;
  tierIndex: number;
  /** The most senior badge and promo code of the press. */
  badgeId: string | null;
  promoCodeId: string | null;
  /** Every tier the press collected, lowest first; never empty. */
  tiers: LadderClaimedTier[];
}

export interface RewardsStats {
  completedTasks: number;
  rewardedUsers: number;
  paidAwardsUsd: number;
}

export interface CollectionCard {
  id: string | null;
  code: string | null;
  title: string;
  image: string | null;
  collected: boolean;
  /** When the player won it; null for a card not held (or an older build). */
  wonAt: Date | null;
  /** The bonus a rare card paid; null on regular cards. */
  bonus: RewardBonus | null;
}

export interface CollectionAlbum {
  /** `{setId}` of the claim route. */
  setId: string;
  code: string;
  title: string;
  description: string | null;
  image: string | null;
  position: number;
  kind: 'REGULAR' | 'RARE' | string;
  total: number;
  collected: number;
  cards: CollectionCard[];
  /** What completing the album pays; `null` — no reward for this album. */
  bonus: RewardBonus | null;
  /** The Album Reward button is live. */
  claimable: boolean;
  /** Set once the reward was taken. */
  claimedAt: Date | null;
}

/** Answer of the Album Reward claim. */
export interface AlbumClaimResult {
  setId: string;
  code: string;
  promoCodeId: string | null;
}

export interface Collection {
  albums: CollectionAlbum[];
  total: number;
  collected: number;
}

export interface ScaleRank {
  index: number;
  title: string;
  threshold: number | null;
  image: string | null;
  /** What reaching the rank pays; `null` — nothing. */
  bonus: RewardBonus | null;
}

export interface ScaleAvatar {
  id: string;
  rankIndex: number;
  position: number;
  image: string | null;
}

/** The counts the guest page shows behind its locks (`/scale` → `ct`, 28.09). */
export interface ScaleCounts {
  onboardingQuests: number;
  /** Quests per tier class, ascending; a class no ladder reaches is absent. */
  questClasses: { tier: number; count: number }[];
  albums: number;
}

export interface Scale {
  ranks: ScaleRank[];
  avatars: ScaleAvatar[];
  /** `null` — a build that does not send `ct`. */
  counts: ScaleCounts | null;
}

export interface RewardBadge {
  id: string;
  code: string;
  group: string;
  rarity: string;
  title: string;
  description: string | null;
  /** The quest card's short text (`sd`); null when none is set — the card shows `description`. */
  summary: string | null;
  emoji: string | null;
  image: string | null;
  points: number;
  owned: boolean;
  awardedAt: Date | null;
  /** Pinned by the player to lead their profile; at most `FEATURED_BADGE_LIMIT`. */
  featured: boolean;
  /** A unique quest awarding it can be earned right now (`lv`, 01.10). */
  live: boolean;
  /** What that quest pays (`bn`); null when it is not live or pays no promo. */
  bonus: RewardBonus | null;
  /**
   * A counted quest's way to its target (`pg`); null for a one-off quest, a
   * badge not live or already held, and when the service could not measure it.
   */
  progress: { current: number; target: number } | null;
}

/**
 * How many achievements the player may pin to the front of their profile —
 * "Choose up to 4 achievements to feature first on your profile"
 * (Figma 13449:480677). The backend enforces the same ceiling.
 */
export const FEATURED_BADGE_LIMIT = 4;

/**
 * The onboarding gate. Until it is passed the service refuses everything past
 * the checklist with 403 `ONBOARDING_REQUIRED` — steps, tier and task claims,
 * pinning badges (29.09) — so the client does not offer them.
 */
export function isOnboardingDone(onboarding: RewardsProfile['onboarding']): boolean {
  return onboarding.done >= onboarding.total;
}

/** The nickname glow: GOLD from the first cup ever obtained, NEON from the first relic. */
export type NameGlow = 'GOLD' | 'NEON';

export function nameGlow(glow: { type: string } | null | undefined): NameGlow | null {
  return glow?.type === 'GOLD' || glow?.type === 'NEON' ? glow.type : null;
}

/**
 * The frames worn over a portrait, in ladder order: Master's ring, Oracle's
 * sparkles, Legend's wreath. A player wears any of them at once (product, 30.10).
 */
export const AVATAR_FRAME_IDS = ['GLOW', 'SPARKLES', 'WREATH'] as const;

export type AvatarFrameId = (typeof AVATAR_FRAME_IDS)[number];

/** A player's look as a user list carries it (`lk`). */
export interface PlayerLook {
  /** `null` — the id-hash picture. */
  avatar: {
    image: string | null;
    rankIndex: number;
    position: number;
    frames: AvatarFrameId[];
  } | null;
  glow: NameGlow | null;
}

/** The hub half of a player's profile, as anyone — a guest included — may see it. */
export interface PlayerCard {
  rank: { index: number; title: string; image: string | null } | null;
  avatar: {
    id: string;
    image: string | null;
    /** Where the portrait sits in the pool — what the local art falls back on while `im` is null. */
    rankIndex: number;
    position: number;
    frames: AvatarFrameId[];
  } | null;
  glow: { type: string; strength: number } | null;
  artifacts: { medals: number; cups: number; diamonds: number };
  /** Badges held in all. */
  achievementsTotal: number;
  /** Up to four for the showcase: the pinned first, then the newest. */
  featured: {
    id: string;
    title: string;
    image: string | null;
    /** Picks the glyph when the badge has no art (the onboarding badges). */
    code: string | null;
    /** `ONBOARDING` picks the onboarding frame. */
    group: string | null;
    /** The frame of every other badge: COMMON / RARE / EPIC / LEGEND. */
    rarity: string | null;
  }[];
}

/** A rare or seasonal task the player can claim now. */
export interface ClaimableTask {
  id: string;
  scopeRef: string;
  badgeId: string;
  title: string;
  bonus: RewardBonus | null;
}

export interface Badges {
  items: RewardBadge[];
  /** Rare/seasonal tasks waiting for their Claim — part of `rc`. */
  claimableTasks: ClaimableTask[];
  total: number;
  owned: number;
  points: number;
}

/** A season task's detector: what it counts. Unknown ones are drawn as a plain metric. */
export type SeasonDetector =
  | 'SEASON_LADDER_METRIC'
  | 'SEASON_EVENTS_TRADED'
  | 'SEASON_TASKS_CLEARED'
  | string;

export interface SeasonTask {
  id: string;
  code: string;
  detector: SeasonDetector;
  title: string;
  points: number;
  badge: { id: string; title: string | null; image: string | null; rarity: string | null } | null;
  bonus: RewardBonus | null;
  /** `null`: a guest, or a measure that failed — drawn without a bar. */
  current: number | null;
  goal: number;
  /** `null` for a guest. */
  status: 'OPEN' | 'CLEARED' | 'CLAIMED' | null;
  description: string | null;
  summary: string | null;
  claimedAt: Date | null;
  /** The Champion's required tasks; `null` for every other task. */
  required: { code: string; title: string; done: boolean }[] | null;
}

export interface Season {
  /** The `scopeRef` of its tasks' claims. */
  id: string;
  title: string;
  daysLeft: number;
  /** Onboarding not finished: the cards are locked and nothing is claimed. */
  locked: boolean;
  tasks: SeasonTask[];
}

export interface SeasonEvent {
  id: string;
  title: string;
  slug: string;
  image: string | null;
  /** `null` for a guest. */
  traded: boolean | null;
}

export interface SeasonEvents {
  minEvents: number;
  events: SeasonEvent[];
}

/**
 * Who the server render drew the hub for (08.10). `unknown`: a session the
 * server could not read — expired, or the profile refused it — so the blocks
 * wait for the browser's session probe, as they did before the server render.
 */
export type RewardsViewer = 'player' | 'guest' | 'unknown';
